import { useTranslation } from 'react-i18next';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Flex } from '@radix-ui/themes';
import { AppShell } from '@/components/layout';
import { isAuthError } from '@/lib/auth';
import { authService } from '@/services/authService';
import { ErrorState } from './ErrorState';

interface FirebaseErrorBoundaryProps {
  children: ReactNode;
}

interface FirebaseErrorBoundaryState {
  error: unknown;
}

function FirebaseErrorBoundaryFallback({ error }: { error: unknown }) {
  const { t } = useTranslation('common');

  const isAuth = isAuthError(error) && error.code.startsWith('auth/');
  const isPermission = isAuthError(error) && error.code === 'permission-denied';

  let title: string;
  let message: string | undefined;
  let label: string;
  if (isAuth) {
    title = t('authError.title');
    message = t('authError.message');
    label = t('authError.action');
  } else if (isPermission) {
    title = t('permissionError.title');
    message = t('permissionError.message');
    label = t('permissionError.action');
  } else {
    title = t('errors.generic');
    message = undefined;
    label = t('actions.retry');
  }

  const handleAction = () => {
    if (isAuth || isPermission) {
      void authService
        .signOut()
        .catch(() => undefined)
        .then(() => {
          window.location.href = '/';
        });
    } else {
      window.location.reload();
    }
  };

  return (
    <AppShell>
      <Flex align="center" justify="center" className="flex-1">
        <ErrorState title={title} message={message} action={{ label, onClick: handleAction }} />
      </Flex>
    </AppShell>
  );
}

export class FirebaseErrorBoundary extends Component<
  FirebaseErrorBoundaryProps,
  FirebaseErrorBoundaryState
> {
  state: FirebaseErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): Partial<FirebaseErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Firebase error boundary caught an error:', error, info);
  }

  render() {
    if (this.state.error) {
      return <FirebaseErrorBoundaryFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}
